import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Carousel from 'react-multi-carousel';
import {
  desktopExtraLargeWidth,
  desktopMediumWidth,
  ipadProWidth,
  largeMobileWidth,
  xLargeMobileWidth,
  smallMobileWidth,
  mediumMobileWidth,
  tabletWidth,
} from '@surfline/quiver-themes';
import { Button, useDeviceSize } from '@surfline/quiver-react';
import { trackEvent } from '@surfline/web-common';
import CarouselCard from '../CarouselCard';
import {
  LARGE_DESKTOP,
  DESKTOP,
  TABLET,
  SMALL_TABLET,
  EXTRA_LARGE_MOBILE,
  LARGE_MOBILE,
  MEDIUM_MOBILE,
  SMALL_MOBILE,
  SIGN_IN,
  HOME,
  CLICKED_POPUP,
  FORCED_REGISTRATION,
  NAVIGATED_TO_CREATE_ACCOUNT,
  NAVIGATED_TO_SIGN_IN,
} from '../../../../common/constants';
import appConfig from '../../../../config';

const BlankCard = () => <div className="blank-card-style" />;

let cards = [
  {
    name: 'favorites',
    imagePath: `${appConfig.cdn}metering/favorites-card.png`,
    header: 'Your Favorites',
    text: 'Creating an account gives you easy access to your favorite spots',
  },
  {
    name: 'cams',
    imagePath: `${appConfig.cdn}metering/cams-card.png`,
    header: '600+ Cams',
    text: 'Access Surfline’s entire network of cameras, including HD & Multi-Cam',
  },
  {
    name: 'reports',
    imagePath: `${appConfig.cdn}metering/reports-card.png`,
    header: '4,000+ Surf Reports',
    text: 'Updated 2X/day, get accurate conditions for spots around the world',
  },
  {
    name: 'forecasts',
    imagePath: `${appConfig.cdn}metering/forecasts-card.png`,
    header: 'Short & Long Term Forecasts',
    text: 'Get Surfline’s expert analysis and detailed 17-day swell outlook',
  },
  {
    name: 'surf-alerts',
    imagePath: `${appConfig.cdn}metering/surf-alerts-card.png`,
    header: 'Surf Alerts',
    text: 'Surfline’s expert analysis gives you day-by-day regional insights',
  },
];
// Numbers of cards to show at each breakpoint. Using slice(0, -2) to remove the ending 'px' string from each breakpoint
const responsive = {
  [LARGE_DESKTOP]: {
    breakpoint: { max: desktopExtraLargeWidth.slice(0, -2), min: desktopMediumWidth.slice(0, -2) },
    items: 5,
  },
  [DESKTOP]: {
    breakpoint: { max: desktopMediumWidth.slice(0, -2), min: ipadProWidth.slice(0, -2) },
    items: 2,
    partialVisibilityGutter: 70,
  },
  [TABLET]: {
    breakpoint: { max: ipadProWidth.slice(0, -2), min: tabletWidth.slice(0, -2) },
    items: 1,
    partialVisibilityGutter: 280,
  },
  [SMALL_TABLET]: {
    breakpoint: { max: tabletWidth.slice(0, -2), min: xLargeMobileWidth.slice(0, -2) },
    items: 2,
    partialVisibilityGutter: 10,
  },
  [EXTRA_LARGE_MOBILE]: {
    breakpoint: { max: xLargeMobileWidth.slice(0, -2), min: largeMobileWidth.slice(0, -2) },
    items: 1,
    partialVisibilityGutter: 30,
  },
  [LARGE_MOBILE]: {
    breakpoint: { max: largeMobileWidth.slice(0, -2), min: mediumMobileWidth.slice(0, -2) },
    items: 1,
    partialVisibilityGutter: 50,
  },
  [MEDIUM_MOBILE]: {
    breakpoint: { max: mediumMobileWidth.slice(0, -2), min: smallMobileWidth.slice(0, -2) },
    items: 1,
    partialVisibilityGutter: 10,
  },
  [SMALL_MOBILE]: {
    breakpoint: { max: smallMobileWidth.slice(0, -2), min: 0 },
    items: 1,
    partialVisibilityGutter: 10,
  },
};

const trackNavigated = (location) =>
  trackEvent(CLICKED_POPUP, {
    action: location === HOME ? NAVIGATED_TO_CREATE_ACCOUNT : NAVIGATED_TO_SIGN_IN,
    category: FORCED_REGISTRATION,
  });

const LearnMore = ({ setForm }) => {
  const { isDesktop } = useDeviceSize();
  const [blankCardsAdded, setBlankCardsAdded] = useState(false);
  if (isDesktop && !blankCardsAdded) {
    cards.push(
      {
        name: 'blankCard',
      },
      {
        name: 'blankCard',
      },
    );
    setBlankCardsAdded(true);
  }
  if (!isDesktop && blankCardsAdded) {
    cards = cards.filter((card) => card.name !== 'blankCard');
  }
  const verifyEmailLinkRef = useRef();
  const [hideRightButton, setHideRightButton] = useState(false);

  const handleNavigation = useCallback(
    (formName) => {
      trackNavigated(formName);
      setForm(formName);
    },
    [setForm],
  );

  const getRightButtonClass = useCallback(
    () =>
      classnames({
        'react-multiple-carousel__arrow': true,
        'react-multiple-carousel__arrow--right': true,
        'sl-meter-multiple-carousel__rightarrow__hide': hideRightButton,
      }),
    [hideRightButton],
  );

  // Customizing the Right Arrow so that it can be placed correctly
  const CustomRightArrow = ({ onClick }) => (
    <button
      type="button"
      aria-label="Right Arrow"
      className={getRightButtonClass()}
      onClick={() => onClick()}
    />
  );

  CustomRightArrow.propTypes = {
    onClick: PropTypes.func.isRequired,
  };

  return (
    <div className="sl-meter-learnmore__container" data-scroll-lock-scrollable="true">
      <h4 className="sl-meter-learnmore__container_title">Key Features:</h4>
      <div className="sl-meter-learnmore__carousel__box">
        <Carousel
          ref={verifyEmailLinkRef}
          customRightArrow={<CustomRightArrow />}
          ssr
          partialVisible
          responsive={responsive}
          removeArrowOnDeviceType={[
            SMALL_MOBILE,
            MEDIUM_MOBILE,
            LARGE_MOBILE,
            EXTRA_LARGE_MOBILE,
            SMALL_TABLET,
            TABLET,
          ]}
          containerClass="sl-meter-learnmore__carousel__container"
          itemClass="sl-meter-learnmore__carousel__item"
          beforeChange={(nextSlide) => {
            if (isDesktop && nextSlide === 4) setHideRightButton(true);
            if (isDesktop && hideRightButton && nextSlide !== 4) setHideRightButton(false);
          }}
        >
          {cards.map((card) => {
            const { name } = card;
            return name === 'blankCard' ? <BlankCard /> : <CarouselCard card={card} />;
          })}
        </Carousel>
      </div>
      <div className="sl-meter-learnmore__container__actions">
        <Button onClick={() => handleNavigation(HOME)}>CREATE FREE ACCOUNT</Button>
        <div className="sl-meter-learnmore__container__actions__signin">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => handleNavigation(SIGN_IN)}
            className="sl-meter-learnmore__container__actions__signin__button"
          >
            Sign In.
          </button>
        </div>
      </div>
    </div>
  );
};

LearnMore.propTypes = {
  setForm: PropTypes.func.isRequired,
};

export default LearnMore;
